import { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Box, ButtonBase, CircularProgress, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, Copy, Lock, RotateCw, Users, X } from "lucide-react";

import { Dialog, DialogContent } from "../../components/Dialog";
import { Avatar } from "../../components/Avatar";
import { Button } from "../../components/Button";
import { CustomTextField } from "../../components/CustomTextField";
import { useTheme } from "../../theme/useTheme";
import { BADGE_TONES } from "../../theme/badgeTones";
import apiClient from "../../api-client.js";

const ROLE_LABEL = {
  "Admin de Agencia": "Administrador",
  Consultor: "Consultor",
};

const PACKS = {
  local: { label: "Starter", tone: "neutral" },
  basic: { label: "Basic", tone: "neutral" },
  ecommerce: { label: "E-commerce", tone: "yellow" },
  infoproducto: { label: "Infoproducto", tone: "yellow" },
  premium: { label: "Premium", tone: "active" },
};

// UnifiedModal/Dialog se pinta en z-index 100000; el popper del tooltip debe quedar por encima.
const TOOLTIP_ABOVE_MODAL = { popper: { sx: { zIndex: 100001 } } };

const REFRESHED_ENDPOINT_PREFIXES = ["clients", "/clients", "users", "agencies"];
const PAGE_SIZE = 100;

// clients/options/ no expone sector ni pack, así que se pagina el listado completo.
const fetchAllClients = async (query) => {
  const clients = [];
  for (let page = 1; ; page += 1) {
    const { data } = await apiClient.get(`clients/?ordering=name&page_size=${PAGE_SIZE}&page=${page}${query}`);
    const results = Array.isArray(data) ? data : data?.results || [];
    clients.push(
      ...results.map((client) => ({
        id: client.id,
        name: client.name,
        sector: client.sector || "",
        pack: client.setup_variant || null,
        logo: client.logo || null,
        agency_id: client.agency_id,
        agency_name: client.agency_name,
        is_read_only: Boolean(client.is_read_only),
      }))
    );
    if (Array.isArray(data) || !data?.next) return clients;
  }
};

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" });
};

const normalize = (text) =>
  (text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

const byName = (a, b) => a.name.localeCompare(b.name, "es");

const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  document.execCommand("copy");
  textArea.remove();
};

const softAvatarSx = (theme, extra = {}) => ({
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
  border: `1px solid ${theme.palette.grey[200]}`,
  ...extra,
});

const CopyValue = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  const handleCopy = async () => {
    try {
      await copyText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      <Typography
        title={value}
        sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {value}
      </Typography>
      {copied ? (
        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: BADGE_TONES.done.color, flexShrink: 0 }}>
          Copiado
        </Typography>
      ) : (
        <IconButton
          size="small"
          aria-label={`Copiar ${value}`}
          onClick={handleCopy}
          sx={{ p: 0.5, color: "text.secondary", flexShrink: 0 }}
        >
          <Copy size={13} />
        </IconButton>
      )}
    </Box>
  );
};

CopyValue.propTypes = { value: PropTypes.string.isRequired };

const ProfileField = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.5 }}>{label}</Typography>
    <Box sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary", minHeight: 26, display: "flex", alignItems: "center" }}>
      {children}
    </Box>
  </Box>
);

ProfileField.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const CountPill = ({ count, active }) => {
  const { theme } = useTheme();
  return (
    <Box
      component="span"
      sx={{
        ml: 0.75,
        px: 0.75,
        minWidth: 20,
        height: 18,
        borderRadius: "999px",
        display: "inline-grid",
        placeItems: "center",
        fontSize: "0.7rem",
        fontWeight: 600,
        color: active ? theme.palette.common.white : theme.palette.text.secondary,
        backgroundColor: active ? theme.palette.primary.main : theme.palette.grey[100],
      }}
    >
      {count ?? "–"}
    </Box>
  );
};

CountPill.propTypes = {
  count: PropTypes.number,
  active: PropTypes.bool,
};

const PackPill = ({ pack }) => {
  const { theme } = useTheme();
  const info = PACKS[pack];
  const hideOnNarrow = { "@media (max-width: 400px)": { display: "none" } };

  if (!info) {
    return (
      <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", flexShrink: 0, ...hideOnNarrow }}>
        Sin pack
      </Typography>
    );
  }
  const tone =
    info.tone === "neutral"
      ? { color: theme.palette.text.primary, background: BADGE_TONES.muted.background }
      : BADGE_TONES[info.tone];
  return (
    <Box
      component="span"
      sx={{
        px: 1,
        height: 22,
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "999px",
        fontSize: "0.72rem",
        fontWeight: 500,
        color: tone.color,
        backgroundColor: tone.background,
        flexShrink: 0,
        ...hideOnNarrow,
      }}
    >
      {info.label}
    </Box>
  );
};

PackPill.propTypes = { pack: PropTypes.string };

const CheckMark = ({ checked }) => {
  const { theme } = useTheme();
  return (
    <Box
      sx={{
        width: 18,
        height: 18,
        borderRadius: "5px",
        display: "grid",
        placeItems: "center",
        border: `1.5px solid ${checked ? theme.palette.primary.main : theme.palette.grey[400]}`,
        backgroundColor: checked ? theme.palette.primary.main : "transparent",
        color: theme.palette.common.white,
        transition: "all 0.15s",
      }}
    >
      {checked && <Check size={12} strokeWidth={3} />}
    </Box>
  );
};

CheckMark.propTypes = { checked: PropTypes.bool };

const ListSkeleton = () => (
  <Box sx={{ px: 1 }}>
    {[0, 1, 2, 3].map((i) => (
      <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25 }}>
        <Skeleton variant="rounded" width={18} height={18} />
        <Skeleton variant="rounded" width={34} height={34} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width="45%" />
          <Skeleton variant="text" width="25%" sx={{ fontSize: "0.7rem" }} />
        </Box>
      </Box>
    ))}
  </Box>
);

const ListMessage = ({ icon: Icon, title, subtitle, action }) => (
  <Box sx={{ textAlign: "center", py: 5, px: 2, color: "text.secondary" }}>
    <Box sx={{ display: "flex", justifyContent: "center", opacity: 0.5 }}>
      <Icon size={22} />
    </Box>
    <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary", mt: 1 }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: "0.8rem", mt: 0.25 }}>{subtitle}</Typography>}
    {action && <Box sx={{ mt: 1.5, display: "flex", justifyContent: "center" }}>{action}</Box>}
  </Box>
);

ListMessage.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  action: PropTypes.node,
};

export const MemberModal = ({
  member,
  agency,
  open,
  onClose,
  onCreateIncident,
  canCreateIncidents = false,
  canManageClients = false,
  onNotify,
  onToggleActive,
  togglingActive = false,
}) => {
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [draft, setDraft] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [pendingLeave, setPendingLeave] = useState(null);
  const [confirmToggle, setConfirmToggle] = useState(false);
  const [section, setSection] = useState(null);

  const isConsultant = member?.role === "Consultor";
  const memberAgencies = useMemo(() => member?.agencies || [], [member]);
  const memberAgencyIds = useMemo(() => new Set(memberAgencies.map((a) => a.id)), [memberAgencies]);
  const isMultiAgency = memberAgencies.length > 1;
  const singleAgencyId = memberAgencies.length === 1 ? memberAgencies[0].id : null;
  const assignedKey = ["team-member-clients", member?.id];

  const assignedQuery = useQuery({
    queryKey: assignedKey,
    queryFn: () => fetchAllClients(`&consultant=${member.id}`),
    enabled: open && Boolean(member?.id) && isConsultant,
  });

  const agencyClientsQuery = useQuery({
    queryKey: ["team-member-agency-clients", singleAgencyId],
    queryFn: () => fetchAllClients(singleAgencyId ? `&agency=${singleAgencyId}` : ""),
    enabled: open && isConsultant && canManageClients,
  });

  const assignedClients = useMemo(() => assignedQuery.data || [], [assignedQuery.data]);
  const assignedIds = useMemo(() => new Set(assignedClients.map((c) => c.id)), [assignedClients]);
  const unassignedClients = useMemo(
    () =>
      (agencyClientsQuery.data || []).filter(
        (client) =>
          !assignedIds.has(client.id) && (memberAgencyIds.size === 0 || memberAgencyIds.has(client.agency_id))
      ),
    [agencyClientsQuery.data, assignedIds, memberAgencyIds]
  );

  const term = normalize(search.trim());
  const matches = (client) =>
    !term || [client.name, client.sector, PACKS[client.pack]?.label].some((field) => normalize(field).includes(term));
  // Asignados (estado guardado) primero; solo cambia al cargar o guardar, no al marcar.
  const allClients = useMemo(
    () => (canManageClients ? [...assignedClients, ...unassignedClients] : assignedClients),
    [canManageClients, assignedClients, unassignedClients]
  );
  const visibleClients = allClients.filter(matches);
  const savedAssignedCount = assignedQuery.isSuccess ? assignedClients.length : undefined;

  const draftEntries = Object.values(draft);
  const draftAdds = draftEntries.filter((d) => d.assign).length;
  const draftRemoves = draftEntries.length - draftAdds;
  const hasDraft = draftEntries.length > 0;

  const toggleDraft = (clients, wantAssigned) =>
    setDraft((prev) => {
      const next = { ...prev };
      clients
        .filter((client) => !client.is_read_only)
        .forEach((client) => {
          if (assignedIds.has(client.id) === wantAssigned) delete next[client.id];
          else next[client.id] = { client, assign: wantAssigned };
        });
      return next;
    });

  const isChecked = (client) => draft[client.id]?.assign ?? assignedIds.has(client.id);

  const refreshRelatedQueries = () => {
    queryClient.invalidateQueries({
      predicate: ({ queryKey }) =>
        queryKey[0] === "getData" &&
        typeof queryKey[1] === "string" &&
        REFRESHED_ENDPOINT_PREFIXES.some((prefix) => queryKey[1].startsWith(prefix)),
    });
    queryClient.invalidateQueries({ queryKey: ["team-member-agency-clients"] });
  };

  const handleSave = async () => {
    if (!hasDraft || isSaving) return;
    setIsSaving(true);
    const results = await Promise.allSettled(
      draftEntries.map(({ client, assign }) =>
        apiClient.post(`clients/${client.id}/${assign ? "add-consultant" : "remove-consultant"}/`, {
          consultant_id: member.id,
        })
      )
    );
    const succeeded = draftEntries.filter((_, i) => results[i].status === "fulfilled");
    const failed = draftEntries.filter((_, i) => results[i].status === "rejected");

    if (succeeded.length) {
      const added = succeeded.filter((d) => d.assign).map((d) => d.client);
      const removedIds = new Set(succeeded.filter((d) => !d.assign).map((d) => d.client.id));
      const addedIds = new Set(added.map((c) => c.id));
      queryClient.setQueryData(assignedKey, (prev = []) =>
        [...prev.filter((c) => !removedIds.has(c.id) && !addedIds.has(c.id)), ...added].sort(byName)
      );
      refreshRelatedQueries();
    }
    setDraft(Object.fromEntries(failed.map((d) => [d.client.id, d])));
    setIsSaving(false);

    if (failed.length) {
      const names = failed.map((d) => d.client.name).join(", ");
      onNotify?.(`No se pudieron guardar ${failed.length} cambios: ${names}`, true);
    } else {
      onNotify?.("Clientes actualizados", false);
    }
  };

  const resetAndClose = () => {
    setSection(null);
    setSearch("");
    setDraft({});
    setPendingLeave(null);
    setConfirmToggle(false);
    onClose();
  };

  const guardUnsaved = (action) => {
    if (isSaving) return;
    if (hasDraft) setPendingLeave(() => action);
    else action();
  };

  const handleClose = () => guardUnsaved(resetAndClose);

  const handleSectionChange = (value) => {
    if (value === "profile") {
      guardUnsaved(() => {
        setDraft({});
        setSection("profile");
      });
    } else {
      setSection(value);
    }
  };

  if (!member) return null;

  const isActive = member.status !== "inactive";
  const statusTone = isActive ? BADGE_TONES.done : BADGE_TONES.muted;
  const agencyNames = memberAgencies.length ? memberAgencies.map((a) => a.name) : [agency].filter(Boolean);
  const subtitle = [
    ROLE_LABEL[member.role] || member.role,
    agencyNames.join(", "),
    formatDate(member.joinDate) && `Alta ${formatDate(member.joinDate)}`,
  ]
    .filter(Boolean)
    .join(" · ");

  const activeSection = isConsultant ? section ?? (canManageClients ? "clients" : "profile") : "profile";
  const sections = [
    { value: "profile", label: "Perfil" },
    { value: "clients", label: "Clientes", count: savedAssignedCount },
  ];
  const renderRow = (client) => {
    const interactive = canManageClients && !client.is_read_only && !isSaving;
    const checked = isChecked(client);
    const change = draft[client.id];
    const details = client.is_read_only
      ? "Solo lectura"
      : [client.sector, isMultiAgency && client.agency_name].filter(Boolean).join(" · ");
    return (
      <ButtonBase
        key={client.id}
        component="div"
        disableRipple
        role={canManageClients ? "checkbox" : undefined}
        aria-checked={canManageClients ? checked : undefined}
        disabled={!interactive}
        onClick={() => toggleDraft([client], !checked)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1.5,
          px: 1,
          py: 1,
          minHeight: 52,
          borderRadius: "10px",
          textAlign: "left",
          cursor: interactive ? "pointer" : "default",
          transition: "background-color 0.15s",
          "&:hover": interactive ? { backgroundColor: theme.palette.grey[100] } : {},
          "&.Mui-disabled": { opacity: 1 },
        }}
      >
        {canManageClients && (
          <Tooltip
            title={client.is_read_only ? "Cliente en modo solo lectura" : checked ? "Desasignar" : "Asignar"}
            placement="top"
            arrow
            slotProps={TOOLTIP_ABOVE_MODAL}
          >
            <Box sx={{ width: 18, display: "grid", placeItems: "center", flexShrink: 0 }}>
              {client.is_read_only ? <Lock size={14} color={theme.palette.text.disabled} /> : <CheckMark checked={checked} />}
            </Box>
          </Tooltip>
        )}
        <Avatar
          name={client.name}
          src={client.logo || undefined}
          variant="rounded"
          sx={softAvatarSx(theme, { width: 34, height: 34, borderRadius: "8px" })}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {client.name}
          </Typography>
          {details && (
            <Typography
              sx={{ fontSize: "0.75rem", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {details}
            </Typography>
          )}
        </Box>
        {change && (
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: "0.72rem", fontWeight: 500, color: "text.secondary", flexShrink: 0 }}
          >
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: theme.palette.primary.main }} />
            {change.assign ? "Se añadirá" : "Se quitará"}
          </Box>
        )}
        <PackPill pack={client.pack} />
      </ButtonBase>
    );
  };

  const renderProfile = () => (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
          columnGap: 4,
          rowGap: 2.25,
        }}
      >
        <ProfileField label="Email">{member.email ? <CopyValue value={member.email} /> : "—"}</ProfileField>
        <ProfileField label="Teléfono">{member.phone ? <CopyValue value={member.phone} /> : "—"}</ProfileField>
        <ProfileField label="Rol">{ROLE_LABEL[member.role] || member.role}</ProfileField>
        <ProfileField label={agencyNames.length > 1 ? "Agencias" : "Agencia"}>{agencyNames.join(", ") || "—"}</ProfileField>
        <ProfileField label="Fecha de alta">{formatDate(member.joinDate) || "—"}</ProfileField>
        {isConsultant && (
          <ProfileField label="Clientes asignados">
            {assignedQuery.isSuccess ? assignedClients.length : member.stats?.totalClients ?? "—"}
          </ProfileField>
        )}
      </Box>
      {!isConsultant && (
        <Typography
          sx={{ mt: 3, p: 1.5, borderRadius: "10px", fontSize: "0.82rem", color: "text.secondary", backgroundColor: theme.palette.grey[50] }}
        >
          Los administradores de agencia tienen acceso a todos los clientes de su agencia, no necesitan asignación.
        </Typography>
      )}
    </>
  );

  const renderBody = () => {
    const queries = canManageClients ? [assignedQuery, agencyClientsQuery] : [assignedQuery];
    if (queries.some((q) => q.isLoading)) return <ListSkeleton />;
    if (queries.some((q) => q.isError)) {
      return (
        <ListMessage
          icon={AlertTriangle}
          title="No se pudieron cargar los clientes"
          action={
            <Button variant="outline" size="sm" onClick={() => queries.forEach((q) => q.refetch())} sx={{ gap: 0.5 }}>
              <RotateCw size={14} /> Reintentar
            </Button>
          }
        />
      );
    }
    if (!assignedClients.length && !unassignedClients.length) {
      return <ListMessage icon={Users} title={canManageClients ? "No hay clientes en la agencia" : "Sin clientes asignados"} />;
    }

    if (!visibleClients.length) {
      return <ListMessage icon={Users} title="Ningún cliente coincide con la búsqueda" />;
    }
    return visibleClients.map((client) => renderRow(client));
  };

  const renderToolbar = () => {
    if (!canManageClients || !allClients.length) return null;
    const editable = visibleClients.filter((c) => !c.is_read_only);
    const toCheck = editable.filter((c) => !isChecked(c));
    const toUncheck = editable.filter((c) => isChecked(c));
    const actionSx = {
      fontSize: "0.72rem",
      fontWeight: 500,
      color: "text.secondary",
      borderRadius: "6px",
      px: 0.75,
      py: 0.25,
      "&:hover": { backgroundColor: theme.palette.grey[100], color: "text.primary" },
      "&.Mui-disabled": { color: "text.disabled" },
    };
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
        <Box component="span" sx={{ fontSize: "0.72rem", fontWeight: 600, color: "text.secondary" }}>
          Clientes asignados ({allClients.filter(isChecked).length})
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", mr: -0.75, color: "text.disabled", fontSize: "0.72rem" }}>
          <ButtonBase disabled={!toCheck.length || isSaving} onClick={() => toggleDraft(toCheck, true)} sx={actionSx}>
            Marcar todos
          </ButtonBase>
          ·
          <ButtonBase disabled={!toUncheck.length || isSaving} onClick={() => toggleDraft(toUncheck, false)} sx={actionSx}>
            Desmarcar todos
          </ButtonBase>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose} maxWidth="sm" fullWidth sx={{ zIndex: 100000 }}>
      <DialogContent
        sx={{ p: 0, position: "relative", display: "flex", flexDirection: "column", maxHeight: "90vh", overflow: "hidden" }}
      >
        <Box
          sx={{
            position: "relative",
            px: 3,
            pt: 3,
            flexShrink: 0,
            ...(!isConsultant && { pb: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }),
          }}
        >
          <IconButton aria-label="Cerrar modal" onClick={handleClose} sx={{ position: "absolute", top: 12, right: 12 }}>
            <X size={20} />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, pr: 5 }}>
            <Avatar
              name={member.name}
              src={member.profile_image || undefined}
              sx={softAvatarSx(theme, { width: 60, height: 60, fontSize: "1.15rem", fontWeight: 600 })}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
                <Typography sx={{ fontSize: "1.25rem", fontWeight: 700, lineHeight: 1.25, color: "text.primary" }}>
                  {member.name}
                </Typography>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1,
                    height: 22,
                    borderRadius: "999px",
                    fontSize: "0.72rem",
                    fontWeight: 500,
                    color: statusTone.color,
                    backgroundColor: statusTone.background,
                  }}
                >
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: "currentColor" }} />
                  {isActive ? "Activo" : "Inactivo"}
                </Box>
              </Box>
              <Typography sx={{ fontSize: "0.82rem", color: "text.secondary", mt: 0.5 }}>{subtitle}</Typography>
            </Box>
          </Box>

          {isConsultant && (
            <Box role="tablist" sx={{ display: "flex", gap: 3, mt: 2.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              {sections.map((t) => {
                const active = t.value === activeSection;
                return (
                  <ButtonBase
                    key={t.value}
                    role="tab"
                    aria-selected={active}
                    onClick={() => handleSectionChange(t.value)}
                    sx={{
                      pb: 1.25,
                      mb: "-1px",
                      fontSize: "0.875rem",
                      fontWeight: active ? 600 : 500,
                      color: active ? "text.primary" : "text.secondary",
                      borderBottom: `2px solid ${active ? theme.palette.primary.main : "transparent"}`,
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    {t.label}
                    {t.value === "clients" && <CountPill count={t.count} active={active} />}
                  </ButtonBase>
                );
              })}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            ...(isConsultant ? { height: "min(560px, 70vh)" } : { flex: 1 }),
            overflowY: "auto",
            px: 3,
            pt: isConsultant ? 2.5 : 2,
            pb: 2,
          }}
        >
          {activeSection === "profile" ? (
            renderProfile()
          ) : (
            <>
              <Box sx={{ mb: 1.5 }}>
                <CustomTextField
                  placeholder="Buscar por nombre, sector o pack"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  isSearch
                  sx={{ width: "100%" }}
                />
              </Box>
              {renderToolbar()}
              <Box sx={{ mx: -1, opacity: isSaving ? 0.6 : 1, transition: "opacity 0.15s" }}>{renderBody()}</Box>
            </>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 3,
            py: 1.75,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {activeSection === "clients" && hasDraft ? (
            <>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", whiteSpace: "nowrap" }}>
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                    {draftEntries.length} {draftEntries.length === 1 ? "cambio sin guardar" : "cambios sin guardar"}
                  </Box>
                  {" · "}
                </Box>
                {[draftAdds && `+${draftAdds}`, draftRemoves && `−${draftRemoves}`].filter(Boolean).join(" ")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button variant="outline" disabled={isSaving} onClick={() => setDraft({})}>
                  Descartar
                </Button>
                <Button onClick={handleSave} aria-busy={isSaving} sx={{ gap: 1, minWidth: 150, ...(isSaving && { pointerEvents: "none" }) }}>
                  {isSaving && <CircularProgress size={14} thickness={5} sx={{ color: "inherit" }} />}
                  {isSaving ? "Guardando..." : "Guardar cambios"}
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                {activeSection === "clients" && assignedQuery.isSuccess && (
                  <>
                    <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                      {assignedClients.length}
                    </Box>
                    {canManageClients && allClients.length > 0 && (
                      <>
                        {" de "}
                        <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                          {allClients.length}
                        </Box>
                      </>
                    )}{" "}
                    {canManageClients && allClients.length > 0
                      ? `clientes asignados a ${member.name.split(" ")[0]}`
                      : assignedClients.length === 1
                        ? "cliente asignado"
                        : "clientes asignados"}
                  </>
                )}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {canCreateIncidents && isConsultant && activeSection === "clients" && (
                  <Button
                    variant="outline"
                    onClick={() =>
                      guardUnsaved(() => {
                        onCreateIncident?.(member);
                        resetAndClose();
                      })
                    }
                    sx={{ gap: 1 }}
                  >
                    <AlertTriangle size={16} />
                    Crear incidencia
                  </Button>
                )}
                {onToggleActive && (
                  <Button
                    variant="ghost"
                    disabled={togglingActive}
                    onClick={() => setConfirmToggle(true)}
                    sx={isActive ? { color: "error.main" } : undefined}
                  >
                    {isActive ? "Desactivar usuario" : "Reactivar usuario"}
                  </Button>
                )}
                <Button variant="outline" onClick={handleClose}>
                  Cerrar
                </Button>
              </Box>
            </>
          )}
        </Box>

        {(pendingLeave || confirmToggle) && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              zIndex: 2,
              display: "grid",
              placeItems: "center",
              p: 3,
              backgroundColor: "rgba(255,255,255,0.72)",
              backdropFilter: "blur(2px)",
            }}
          >
            <Box
              role="alertdialog"
              aria-labelledby="member-modal-confirm-title"
              sx={{
                width: "100%",
                maxWidth: 360,
                p: 2.5,
                borderRadius: "12px",
                backgroundColor: "background.paper",
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[8],
              }}
            >
              <Typography id="member-modal-confirm-title" sx={{ fontSize: "0.95rem", fontWeight: 600 }}>
                {confirmToggle
                  ? isActive
                    ? `¿Desactivar a ${member.name}?`
                    : `¿Reactivar a ${member.name}?`
                  : "Tienes cambios sin guardar"}
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary", mt: 0.5 }}>
                {confirmToggle
                  ? isActive
                    ? "Dejará de poder entrar en Markezy y su plaza quedará libre. No se borra nada: sus clientes, mensajes y horas se conservan."
                    : "Volverá a poder entrar en Markezy y ocupará una plaza de tu plan."
                  : "¿Quieres descartarlos? Los clientes asignados no se modificarán."}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2.5 }}>
                <Button
                  variant="outline"
                  disabled={togglingActive}
                  onClick={() => (confirmToggle ? setConfirmToggle(false) : setPendingLeave(null))}
                >
                  {confirmToggle ? "Cancelar" : "Seguir editando"}
                </Button>
                {confirmToggle ? (
                  <Button
                    variant={isActive ? "destructive" : "default"}
                    disabled={togglingActive}
                    onClick={async () => {
                      await onToggleActive(member);
                      setConfirmToggle(false);
                    }}
                  >
                    {togglingActive ? "Guardando…" : isActive ? "Desactivar" : "Reactivar"}
                  </Button>
                ) : (
                  <Button
                    variant="dark"
                    onClick={() => {
                      const action = pendingLeave;
                      setPendingLeave(null);
                      setDraft({});
                      action();
                    }}
                  >
                    Descartar
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

MemberModal.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    email: PropTypes.string,
    phone: PropTypes.string,
    joinDate: PropTypes.string,
    status: PropTypes.string,
    profile_image: PropTypes.string,
    agencies: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    stats: PropTypes.shape({ totalClients: PropTypes.number }),
  }),
  agency: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onToggleActive: PropTypes.func,
  togglingActive: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onCreateIncident: PropTypes.func,
  canCreateIncidents: PropTypes.bool,
  canManageClients: PropTypes.bool,
  onNotify: PropTypes.func,
};
